import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import Modal from '../common/Modal';

// Mutăm FormField în afara componentei pentru a evita re-crearea la fiecare render
const FormField = ({ label, field, type = 'text', required = false, children, formData, errors, onInputChange, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children || (
      <input
        type={type}
        value={formData[field]}
        onChange={(e) => onInputChange(field, e.target.value)}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          errors[field] ? 'border-red-300' : 'border-gray-300'
        }`}
        {...props}
      />
    )}
    {errors[field] && (
      <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
    )}
  </div>
);

const ServiceForm = ({
  service = null,
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'bucată',
    price: '',
    currency: 'RON',
    cpvCode: ''
  });

  const [errors, setErrors] = useState({});

  const units = ['bucată', 'oră', 'zi', 'lună', 'an', 'km', 'kg', 'pachet', 'licență'];
  const currencies = ['RON', 'EUR', 'USD'];

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        description: service.description || '',
        unit: service.unit || 'bucată',
        price: service.price?.toString() || '',
        currency: service.currency || 'RON',
        cpvCode: service.cpvCode || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        unit: 'bucată',
        price: '',
        currency: 'RON',
        cpvCode: ''
      });
    }
    setErrors({});
  }, [service, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Numele serviciului este obligatoriu';
    }

    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) < 0) {
      newErrors.price = 'Prețul trebuie să fie un număr pozitiv';
    }

    if (formData.cpvCode && !/^[0-9]{8}(-[0-9])?$/.test(formData.cpvCode)) {
      newErrors.cpvCode = 'Format cod CPV invalid (ex: 12345678 sau 12345678-1)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price)
      };
      onSubmit(submitData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={service ? 'Editează Serviciu' : 'Serviciu Nou'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label="Nume Serviciu"
          field="name"
          required
          placeholder="ex: Consultanță IT"
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        />

        <FormField
          label="Descriere"
          field="description"
          placeholder="Descrierea serviciului..."
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        >
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.description ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Descrierea serviciului..."
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Unitate de Măsură"
            field="unit"
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          >
            <select
              value={formData.unit}
              onChange={(e) => handleInputChange('unit', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {units.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Monedă"
            field="currency"
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          >
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {currencies.map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField
          label="Preț"
          field="price"
          type="number"
          step="0.01"
          min="0"
          required
          placeholder="0.00"
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        />

        <FormField
          label="Cod CPV"
          field="cpvCode"
          placeholder="ex: 72000000"
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
          >
            {service ? 'Actualizează' : 'Creează'} Serviciu
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ServiceForm;